import {forwardRef, useMemo} from "react";
import {Project} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {Contact} from "../../../../api/Contacts";
import {useUser} from "../../../../misc/Permission";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {CoverPage} from "../../../../misc/pdf/parts/CoverPage";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {preambleToBlocks} from "../../../../misc/richtext/preambleContent";
import {ClientInfoContextProvider, PDFProjectContext} from "../openings/OpeningsPDF";
import {GlazingPageItem} from "./GlazingPagesTab";
import {GlazingSection, GlazingSectionPage} from "./GlazingSectionPage";
import {GlazingSummaryPage, summarizeGlass} from "./GlazingSummary";

export const GlazingPDF = forwardRef(function GlazingPDF(props: {
    project: Project;
    client?: Company;
    clientContact?: Contact;
    notes: string;
    lastSubmittal: string | null;
    screens: GlazingSection[];
    doors: GlazingSection[];
    // the sections to print, as ticked on the submittal dialog.
    pages: GlazingPageItem[];
    onReady(): void;
}, ref: any) {
    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    const notes = useMemo(() => preambleToBlocks(props.notes || ""), [props.notes]);

    const pages = props.pages;
    const screens = useMemo(() => pages.includes("frameGlazing") ? props.screens : [], [pages, props.screens]);
    const doors = useMemo(() => pages.includes("doorGlazing") ? props.doors : [], [pages, props.doors]);

    // the summary covers the glass of the sections that print, so the two always agree.
    const summary = useMemo(() =>
        pages.includes("summary") ? summarizeGlass([...screens, ...doors]) : [],
        [pages, screens, doors]);

    return (
        <div ref={ref}>
            <PDF>
                <PDFProjectContext.Provider value={props.project}>
                    <ClientInfoContextProvider client={props.client} clientContact={props.clientContact}>
                        <CoverPage title="GLAZING SUBMITTAL" lastSubmittal={props.lastSubmittal} />
                        {notes.length > 0 && <PageRenderProvider data={notes} onReady={ready.section()}>
                            {info =>
                                <ContainerPage>
                                    <TitleTextBlock title="NOTES">
                                        {info.rows.map((d, index) => <div key={index.toString()} dangerouslySetInnerHTML={{__html: d}} />)}
                                    </TitleTextBlock>
                                </ContainerPage>}
                        </PageRenderProvider>}
                        {summary.length > 0 && <GlazingSummaryPage rows={summary} onReady={ready.section()} />}
                        {screens.map((s, index) => (
                            <GlazingSectionPage key={"screen-" + index} title="FRAME GLAZING" section={s} />
                        ))}
                        {doors.map((s, index) => (
                            <GlazingSectionPage key={"door-" + index} title="DOOR GLAZING" section={s} />
                        ))}
                    </ClientInfoContextProvider>
                </PDFProjectContext.Provider>
            </PDF>
        </div>
    );
});
